import { useState, type DragEvent } from 'react'

const SORT_TYPE = 'application/x-emili-sort'
export const PRODUCT_DRAG_TYPE = 'application/x-emili-product'

/** Presunie položku `dragId` na miesto `targetId` (nadol → za cieľ, nahor → pred cieľ). */
export function moveId(ids: readonly string[], dragId: string, targetId: string): string[] {
  const from = ids.indexOf(dragId)
  const to = ids.indexOf(targetId)
  if (from < 0 || to < 0 || from === to) return [...ids]
  const next = ids.filter(id => id !== dragId)
  next.splice(to, 0, dragId)
  return next
}

/**
 * Zoraďovanie potiahnutím myšou (HTML5 drag & drop) – bez knižníc.
 * `group` oddeľuje zoznamy, aby sa položka nedala pustiť do iného zoznamu.
 * Pri `handle: true` sa riadok dá chytiť iba za úchyt (⋮⋮) – vhodné pre riadky s poliami formulára.
 */
export function useDragSort(group: string, ids: readonly string[], onReorder: (ids: string[]) => void, options: { handle?: boolean; disabled?: boolean; canDrop?: (dragId: string, targetId: string) => boolean } = {}) {
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const [armedId, setArmedId] = useState<string | null>(null)
  const reset = () => { setDragId(null); setOverId(null); setArmedId(null) }

  const itemProps = (id: string) => ({
    draggable: !options.disabled && (!options.handle || armedId === id),
    'data-dragging': dragId === id ? 'true' : undefined,
    'data-drop-target': overId === id && dragId !== null && dragId !== id ? (ids.indexOf(dragId) < ids.indexOf(id) ? 'after' : 'before') : undefined,
    onDragStart: (event: DragEvent) => {
      if (options.disabled || (options.handle && armedId !== id)) return
      event.stopPropagation()
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData(SORT_TYPE, `${group}:${id}`)
      event.dataTransfer.setData('text/plain', '')
      setDragId(id)
    },
    onDragOver: (event: DragEvent) => {
      if (!dragId || !event.dataTransfer.types.includes(SORT_TYPE)) return
      if (options.canDrop && !options.canDrop(dragId, id)) { if (overId) setOverId(null); return }
      event.preventDefault()
      event.stopPropagation()
      event.dataTransfer.dropEffect = 'move'
      if (overId !== id) setOverId(id)
    },
    onDragLeave: (event: DragEvent) => {
      if (overId === id && !(event.currentTarget as Node).contains(event.relatedTarget as Node | null)) setOverId(null)
    },
    onDrop: (event: DragEvent) => {
      if (!dragId || !event.dataTransfer.types.includes(SORT_TYPE)) return
      if (options.canDrop && !options.canDrop(dragId, id)) return
      event.preventDefault()
      event.stopPropagation()
      const [dropGroup] = event.dataTransfer.getData(SORT_TYPE).split(':')
      const next = moveId(ids, dragId, id)
      reset()
      if (dropGroup === group && next.join() !== ids.join()) onReorder(next)
    },
    onDragEnd: reset,
  })

  const handleProps = (id: string) => ({
    className: 'drag-handle',
    title: 'Potiahni a presuň',
    'aria-hidden': true as const,
    onMouseDown: () => setArmedId(id),
    onMouseUp: () => setArmedId(null),
    onTouchStart: () => setArmedId(id),
  })

  return { itemProps, handleProps, dragging: dragId !== null }
}
